import type { CSSProperties, ReactNode } from 'react';

const palette = {
  background: '#f8fafc',
  border: '#dbe3ef',
  borderStrong: '#c5d1e0',
  text: '#0f172a',
  muted: '#475569',
  primary: '#2697a2',
  primaryDark: '#1d7179',
  primarySoft: '#ecfdf5',
  warningSoft: '#fff7ed',
  warningBorder: '#fdba74',
  warningText: '#9a3412',
  errorSoft: '#fef2f2',
  errorBorder: '#fecaca',
  errorText: '#b91c1c',
  infoSoft: '#eff6ff',
  infoBorder: '#bfdbfe',
  infoText: '#1d4ed8',
} as const;

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

const fieldControlClass =
  'block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 px-3 transition-colors border';

const buttonBaseClass =
  'inline-flex items-center px-4 py-2 text-xs font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary uppercase tracking-wider whitespace-nowrap';

const buttonVariantClass = {
  primary: 'bg-primary text-white hover:bg-primary-hover shadow-sm border border-transparent',
  secondary: 'border border-primary text-primary bg-white hover:bg-[#EAF8F4] shadow-sm',
  danger: 'border border-[#fecaca] text-[#b91c1c] bg-white hover:bg-[#fef2f2] shadow-sm',
  ghost: 'text-text-secondary hover:text-irongrey bg-transparent',
} as const;

export const stackStyle = (gap = 16): CSSProperties => ({
  display: 'grid',
  gap,
});

export const gridStyle = (columns = 2): CSSProperties => ({
  display: 'grid',
  gap: 16,
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
});

export const SettingsPanel = ({
  title,
  description,
  actions,
  className,
  testId,
  style,
  children,
}: {
  title: string;
  eyebrow?: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
  testId?: string;
  style?: CSSProperties;
  children: ReactNode;
}) => (
  <div className={cx('box-border min-w-0 max-w-full w-full bg-white shadow-sm rounded-lg border border-border p-6', className)} data-testid={testId} style={style}>
    <div className="flex min-w-0 justify-between items-start mb-6">
      <div className="min-w-0">
        <h3 className="text-lg font-semibold leading-6 text-irongrey">
          {title}
        </h3>
        {description ? (
          <p className="text-sm text-text-secondary mt-1">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div>{actions}</div> : null}
    </div>
    {children}
  </div>
);

export const Field = ({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: ReactNode;
}) => (
  <label className="block">
    <span className="block text-sm font-medium text-irongrey mb-1">{label}</span>
    {children}
    {helper ? <div className="mt-1 text-xs text-text-secondary">{helper}</div> : null}
  </label>
);

export const TextInput = ({ className, style, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={cx(fieldControlClass, className)} style={style} />
);

export const TextArea = ({ className, style, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...props}
    className={cx(fieldControlClass, 'min-h-[108px] resize-y', className)}
    style={style}
  />
);

export const SelectInput = ({ className, style, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...props} className={cx(fieldControlClass, className)} style={style} />
);

export const ToggleField = ({
  label,
  checked,
  onChange,
  helper: _helper,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helper?: string;
}) => (
  <div className="flex items-center justify-between py-4">
    <span className="text-sm font-medium text-irongrey">{label}</span>
    <div className="flex items-center gap-3">
      <button
        aria-checked={checked}
        className={cx(
          checked ? 'bg-primary' : 'bg-border',
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
        )}
        onClick={() => onChange(!checked)}
        role="switch"
        type="button"
      >
        <span
          aria-hidden="true"
          className={cx(
            checked ? 'translate-x-5' : 'translate-x-0',
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
          )}
        />
      </button>
    </div>
  </div>
);

export const ActionButton = ({
  variant = 'secondary',
  href,
  children,
  className,
  style,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  href?: string;
}) => {
  const buttonClassName = cx(
    buttonBaseClass,
    buttonVariantClass[variant],
    props.disabled && 'cursor-not-allowed opacity-60',
    className
  );

  if (href) {
    return (
      <a className={buttonClassName} href={href} rel="noreferrer" style={style} target="_blank">
        {children}
      </a>
    );
  }

  return (
    <button
      {...props}
      className={buttonClassName}
      style={style}
      type={props.type ?? 'button'}
    >
      {children}
    </button>
  );
};

export const StatusPill = ({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'error' | 'neutral';
  children: ReactNode;
}) => {
  const colors = tone === 'success'
    ? { background: palette.primarySoft, border: palette.primary, color: palette.primaryDark }
    : tone === 'warning'
      ? { background: palette.warningSoft, border: palette.warningBorder, color: palette.warningText }
      : tone === 'error'
        ? { background: palette.errorSoft, border: palette.errorBorder, color: palette.errorText }
        : tone === 'neutral'
          ? { background: '#f1f5f9', border: palette.borderStrong, color: palette.text }
          : { background: palette.infoSoft, border: palette.infoBorder, color: palette.infoText };

  return (
    <span
      style={{
        background: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 999,
        color: colors.color,
        display: 'inline-flex',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 0.2,
        padding: '6px 10px',
      }}
    >
      {children}
    </span>
  );
};

export const Notice = ({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'error';
  title: string;
  children?: ReactNode;
}) => {
  const colors = tone === 'success'
    ? { background: palette.primarySoft, border: palette.primary, color: palette.primaryDark }
    : tone === 'warning'
      ? { background: palette.warningSoft, border: palette.warningBorder, color: palette.warningText }
      : tone === 'error'
        ? { background: palette.errorSoft, border: palette.errorBorder, color: palette.errorText }
        : { background: palette.infoSoft, border: palette.infoBorder, color: palette.infoText };

  return (
    <div
      style={{
        background: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
        color: colors.color,
        display: 'grid',
        gap: 6,
        padding: '12px 14px',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 800 }}>{title}</div>
      {children ? <div style={{ fontSize: 13, lineHeight: 1.5 }}>{children}</div> : null}
    </div>
  );
};

export const KeyValueGrid = ({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>;
}) => (
  <div style={{ display: 'grid', gap: 10 }}>
    {items.map((item) => (
      <div
        key={String(item.label)}
        style={{
          alignItems: 'flex-start',
          borderBottom: `1px solid ${palette.border}`,
          display: 'flex',
          gap: 12,
          justifyContent: 'space-between',
          paddingBottom: 10,
        }}
      >
        <span style={{ color: palette.muted, fontSize: 12, fontWeight: 800, letterSpacing: 0.25, textTransform: 'uppercase' }}>
          {item.label}
        </span>
        <span style={{ color: palette.text, fontSize: 14, maxWidth: '65%', textAlign: 'right' }}>{item.value}</span>
      </div>
    ))}
  </div>
);

export const DataTable = ({
  columns,
  rows,
}: {
  columns: string[];
  rows: ReactNode[][];
}) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%', width: '100%' }}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column}
              style={{
                borderBottom: `1px solid ${palette.borderStrong}`,
                color: palette.muted,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.25,
                padding: '0 0 10px',
                textAlign: 'left',
                textTransform: 'uppercase',
              }}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`row-${rowIndex}`}>
            {row.map((cell, cellIndex) => (
              <td
                key={`cell-${rowIndex}-${cellIndex}`}
                style={{
                  borderBottom: `1px solid ${palette.border}`,
                  padding: '12px 0',
                  verticalAlign: 'top',
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
